/**
 * The shape of everything the server must never see.
 *
 * If a field is in this file, it lives encrypted on the device and in the
 * export file, and nowhere else. Adding a field here is free; moving one out
 * of here is a change to the product's central promise.
 */

export const PAYLOAD_VERSION = 1;

/** One step of the ordered recall sequence. */
export interface SequenceStep {
  /** Stable id so a reordered sequence keeps its answers. */
  id: string;
  /** The question the user is asked, in their own words. */
  prompt: string;
  /**
   * Candidate answers. Exactly one is correct; the drill shuffles them at
   * presentation time so position carries no information.
   *
   * The design lists `questionSequence` and `answerOptions` as siblings; they
   * are unified here because an answer set is meaningless apart from its
   * question, and splitting them invites the two arrays to drift out of sync.
   */
  answerOptions: string[];
  correctIndex: number;
}

/** Five hints, shallowest first. Level 5 should get anyone to the backup. */
export type HintLevels = [string, string, string, string, string];

export interface AnchorPayload {
  version: typeof PAYLOAD_VERSION;
  /** What the user calls this backup. Never leaves the device. */
  displayName: string;
  /** "steel plate", "notebook in the safe" — free text, deliberately. */
  storageMedium: string;
  hintLevels: HintLevels;
  questionSequence: SequenceStep[];
  /** The answer. Gated behind a fresh passphrase entry and auto-hidden. */
  exactLocationHint: string;
  createdAt: number;
  updatedAt: number;
}

/** How a KEK wraps the master key. */
export type KekKind = 'passphrase' | 'biometric' | 'session';

export interface WrappedKey {
  kind: KekKind;
  /** base64 nonce */
  iv: string;
  /** base64 wrapped MK */
  ct: string;
  createdAt: number;
}

export interface VaultMetaRecord {
  id: 'meta';
  version: 1;
  /** Argon2id salt for KEK-P, hex. */
  saltHex: string;
  kdf: {
    algorithm: 'argon2id';
    memoryKiB: number;
    iterations: number;
    parallelism: number;
    keyLengthBytes: number;
  };
  /** Always present — KEK-P is mandatory. */
  wrappedMkPassphrase: WrappedKey;
  /** Present once the user has enrolled a WebAuthn PRF credential. */
  wrappedMkBiometric?: WrappedKey;
  /** WebAuthn credential id, base64. Not secret. */
  biometricCredentialId?: string;
  createdAt: number;
  /** Bumped every time the user proves they still have the passphrase. */
  lastPassphraseVerifiedAt: number;
  /** True once the user has downloaded an export at least once. */
  exportedAt: number | null;
}

export interface AnchorRecord {
  /** Same id as the server-side anchor document. The only shared value. */
  id: string;
  /** base64 nonce */
  iv: string;
  /** base64 sealed AnchorPayload */
  ct: string;
  updatedAt: number;
}

/** The decrypted view the UI works with. */
export interface UnlockedAnchor {
  id: string;
  payload: AnchorPayload;
}
